import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { T, Var } from 'gt-react';
import type {
  ComponentRegistry,
  ComponentTypeDefinition,
} from '@/components/report-canvas/registry';
import { builtinChartRegistry } from '@/components/report-canvas/builtinCharts';
import { vegaWidgetRegistry } from '@/components/report-canvas/vega/VegaChartWidget';
import { tableWidgetRegistry } from '@/components/report-canvas/charts/TableWidget';
import { kpiWidgetRegistry } from '@/components/report-canvas/charts/KpiWidget';
import { textWidgetRegistry } from '@/components/report-canvas/charts/TextWidget';
import {
  ReportDataContext,
  useReportDatasets,
} from '@/components/report-canvas/ReportDataContext';
import type { ReportComponentConfig } from '@/components/report-canvas/types';
import type { ReportFilter } from '@erp/shared-types';
import type { WorkbookReportSource } from './useWorkbookReportData';
import {
  serverRowsRequest,
  type ServerRowsRequest,
} from './workbookReportAggregation';
import { withDatasetRows, workbookReportRows } from './workbookReportDataset';
import {
  reportAggregateQueryOptions,
  reportLeadingRowsQueryOptions,
} from './workbookReportDataBatch';

/** What a Report's components need to ask for Server aggregation. */
export type WorkbookReportServer = {
  workbookId: string;
  /** By Report dataset key. */
  sources: ReadonlyMap<string, WorkbookReportSource>;
  /** The Report filters in effect, resolved; the server reads only the rows passing them. */
  filters?: readonly ReportFilter[];
};
export const WorkbookReportServerContext =
  createContext<WorkbookReportServer | null>(null);

function RowsHint({ children }: { children: ReactNode }) {
  return (
    <p className="pointer-events-none absolute bottom-1 left-2 z-10 text-[10px] text-muted-foreground">
      {children}
    </p>
  );
}

/** The rows a server request returns, as the component's dataset reads them. */
function useServerRows(request: ServerRowsRequest | null) {
  // Read in the Report's batch: one request for all its components.
  const leading = useLeadingRows(request);
  const aggregated = useAggregatedRows(request);
  const read = leading.data?.kind === 'rows' ? leading.data : aggregated.data;
  return {
    read,
    truncated: aggregated.data?.truncated ?? false,
    isFetching: leading.isFetching || aggregated.isFetching,
  };
}

function useLeadingRows(request: ServerRowsRequest | null) {
  return useQuery(
    reportLeadingRowsQueryOptions(
      request?.kind === 'leading' ? request.input : skipToken,
    ),
  );
}

function useAggregatedRows(request: ServerRowsRequest | null) {
  return useQuery(
    reportAggregateQueryOptions(
      request?.kind === 'aggregate' ? request.input : skipToken,
    ),
  );
}

/** Says how a component past the Client row limit got its rows, once it has them. */
function ServerRowsNote({
  request,
  shown,
  truncated,
  rowCount,
}: {
  request: ServerRowsRequest;
  /** The rows read, once they arrive. */
  shown: number | undefined;
  truncated: boolean;
  rowCount: string;
}) {
  if (request.kind === 'none')
    return request.summarizable ? null : (
      <RowsHint>
        <T context="workbook report chart needs every row">
          This chart needs every row, and <Var>{rowCount}</Var> rows is too many
          to send. Use a bar, line, pie, radar or scatter chart.
        </T>
      </RowsHint>
    );
  if (shown === undefined) return null;
  return (
    <RowsHint>
      {request.kind === 'leading' ? (
        <T context="workbook report table leading rows">
          First <Var>{shown.toLocaleString()}</Var> of <Var>{rowCount}</Var>{' '}
          rows
        </T>
      ) : truncated ? (
        <T context="workbook report server aggregation truncated">
          Aggregated on server · <Var>{rowCount}</Var> rows · first{' '}
          <Var>{shown.toLocaleString()}</Var> groups
        </T>
      ) : (
        <T context="workbook report server aggregation">
          Aggregated on server · <Var>{rowCount}</Var> rows
        </T>
      )}
    </RowsHint>
  );
}

/**
 * Gives a component whose dataset is past the Client row limit what it charts
 * from the server, as its dataset's rows, so the component reads a Report
 * dataset either way.
 */
function ServerAggregated({
  component,
  children,
}: {
  component: ReportComponentConfig;
  children: ReactNode;
}) {
  const server = useContext(WorkbookReportServerContext);
  const data = useReportDatasets();
  const key = component.datasetKey ?? data.defaultDatasetKey;
  const source = server?.sources.get(key);
  const request = server
    ? serverRowsRequest(server.workbookId, source, component, server.filters)
    : null;
  const { read, truncated, isFetching } = useServerRows(request);
  const value = useMemo(
    () =>
      source
        ? withDatasetRows(
            data,
            key,
            read ? workbookReportRows(source, read.columns, read.rows) : [],
            isFetching,
            request?.kind === 'aggregate',
          )
        : data,
    [data, key, source, read, isFetching, request?.kind],
  );
  if (!request || !source) return children;
  return (
    <ReportDataContext.Provider value={value}>
      <div className="relative flex min-h-0 flex-1 flex-col">
        {children}
        <ServerRowsNote
          request={request}
          shown={read?.rows.length}
          truncated={truncated}
          // The rows read under the Report filters, once known.
          rowCount={(read?.rowCount ?? source.rowCount ?? 0).toLocaleString()}
        />
      </div>
    </ReportDataContext.Provider>
  );
}

/** Wraps a component type's renderer so it charts Server aggregation past the Client row limit. */
function withServerAggregation(
  definition: ComponentTypeDefinition,
): ComponentTypeDefinition {
  const Inner = definition.Renderer;
  function Renderer({ component }: { component: ReportComponentConfig }) {
    return (
      <ServerAggregated component={component}>
        <Inner component={component} />
      </ServerAggregated>
    );
  }
  return { ...definition, Renderer };
}

/**
 * The components a Report offers: KPIs, the builtin charts, the Table, the
 * agent-authorable vega chart and Text blocks, each
 * charting Server aggregation (a Table, its leading rows) when its dataset is
 * past the Client row limit.
 */
export const workbookReportRegistry: ComponentRegistry = {
  ...Object.fromEntries(
    Object.entries({
      ...kpiWidgetRegistry,
      ...builtinChartRegistry,
      ...tableWidgetRegistry,
      ...vegaWidgetRegistry,
    }).map(([type, definition]) => [type, withServerAggregation(definition)]),
  ),
  // Reads no dataset, so never asks the server for rows.
  ...textWidgetRegistry,
};
